import { useEffect, useState } from 'react';
import { IconButton, useMediaQuery } from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Pause from '@mui/icons-material/Pause';
import PlayArrow from '@mui/icons-material/PlayArrow';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import VerifiedUserOutlined from '@mui/icons-material/VerifiedUserOutlined';
import DirectionsBusOutlined from '@mui/icons-material/DirectionsBusOutlined';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import logo from '../assets/images/login.png';
import { loginSlides } from './loginSlides';
import './LoginCarousel.css';

export default function LoginCarousel() {
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [hidden, setHidden] = useState(() => document.hidden);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const paused = hovered || focused || userPaused || hidden || reducedMotion;

  useEffect(() => {
    const updateVisibility = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', updateVisibility);
    return () => document.removeEventListener('visibilitychange', updateVisibility);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => setActive(previous => (previous + 1) % loginSlides.length), 5000);
    return () => window.clearInterval(timer);
  }, [paused, active]);

  const step = delta => setActive(previous => (previous + delta + loginSlides.length) % loginSlides.length);
  return (
    <section className="login-carousel" aria-labelledby="portal-title" aria-roledescription="carousel"
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)} onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
      <header className="login-carousel-branding">
        <img className="login-carousel-logo" src={logo} alt="District Administration RTO Dewas — School Bus and Driver Verification" />
        <div><h1 id="portal-title">RTO Dewas</h1><p>School Transport Safety &amp; Verification Portal</p><p className="login-carousel-sponsor">An initiative supported by RTO Dewas</p></div>
      </header>
      <div className="login-carousel-window" aria-live={paused ? 'polite' : 'off'}>
        {loginSlides.map((slide, index) => (
          <figure key={slide.heading} className={`login-carousel-slide${index === active ? ' is-active' : ''}${slide.photograph ? ' login-carousel-slide--photograph' : ''}`} role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${loginSlides.length}`} aria-hidden={index !== active}>
            <div className="login-carousel-image"><img src={slide.image} alt={slide.alt} width="1200" height="900" decoding="async" style={{ objectFit: slide.imageFit || 'cover' }} /></div>
            <figcaption><h2>{slide.heading}</h2><p lang={slide.lang || "en"}>{slide.tagline}</p>{slide.assetNote && <span className="login-carousel-placeholder">{slide.assetNote}</span>}</figcaption>
          </figure>
        ))}
      </div>
      <div className="login-carousel-features" aria-label="Portal features">
        {[
          [FactCheckOutlined, 'Vehicle Compliance', 'RC, Fitness, Permit, PUC', 'blue'],
          [VerifiedUserOutlined, 'Verified Drivers & Conductors', 'Police Verification', 'mint'],
          [DirectionsBusOutlined, 'Safe School Transport', 'Student Safety First', 'orange'],
          [AssessmentOutlined, 'Digital Monitoring', 'Reports & MIS', 'purple'],
        ].map(([Icon, title, detail, tone]) => <div className={`login-carousel-feature ${tone}`} key={title}><span className="login-carousel-feature-icon"><Icon fontSize="small" /></span><strong>{title}</strong><span>{detail}</span></div>)}
      </div>
      <div className="login-carousel-controls" aria-label="Carousel controls" role="group">
        <IconButton type="button" aria-label="Previous slide" onClick={() => step(-1)} size="small"><ChevronLeft /></IconButton>
        <div className="login-carousel-dots" role="group" aria-label="Choose a slide">
          {loginSlides.map((slide,index) => <button type="button" key={slide.heading} aria-label={`Go to slide ${index + 1}: ${slide.heading}`} aria-current={index === active ? 'true' : undefined} onClick={() => setActive(index)}><span /></button>)}
        </div>
        <IconButton type="button" aria-label="Next slide" onClick={() => step(1)} size="small"><ChevronRight /></IconButton>
        {!reducedMotion && <IconButton type="button" aria-label={userPaused ? 'Resume slideshow' : 'Pause slideshow'} aria-pressed={userPaused} onClick={() => setUserPaused(previous => !previous)} size="small">{userPaused ? <PlayArrow fontSize="small" /> : <Pause fontSize="small" />}</IconButton>}
      </div>
    </section>
  );
}
